import { execAdmin } from '@src/apis/reware/utils';

import type {
  PricingExportResult,
  GenerateResalePricingExportExecVariables,
  GenerateResalePricingExportExecResponse,
} from './types';

export interface GenerateResalePricingExportArgs {
  partnerId: number;
  id: number;
}

/**
 * Requests a TSV export of a pricing snapshot's rules.
 *
 * Returns a presigned URL plus its `expiresAt`. The caller (data provider →
 * component) opens the URL in a new tab so the browser downloads directly — the
 * TSV bytes never pass through the GraphQL response or React state.
 *
 * Server-side details (where the bytes are staged, the URL's TTL, how the
 * filename is built) are owned by reware-api and intentionally not asserted
 * here, since this frontend can't verify them.
 *
 * @param id - The `PricingDiscount.id` of the snapshot to export.
 */
export const generateResalePricingExport = async ({
  partnerId,
  id,
}: GenerateResalePricingExportArgs): Promise<PricingExportResult> => {
  const mutation = `
    mutation generateResalePricingExport($partnerId: Int!, $id: Int!) {
      partner(id: $partnerId) {
        generateResalePricingExport(id: $id) {
          url
          expiresAt
        }
      }
    }
  `;

  const data = await execAdmin<
    GenerateResalePricingExportExecVariables,
    GenerateResalePricingExportExecResponse
  >(mutation, { variables: { partnerId, id } });

  const result = data.partner?.generateResalePricingExport;
  if (!result) {
    throw new Error(
      `generateResalePricingExport: unexpected response — partner.generateResalePricingExport is ${
        result === null ? 'null' : 'undefined'
      } (partnerId=${partnerId}, id=${id})`,
    );
  }

  return result;
};
